/**
 * 행 컬렉션 등록형(registered) 도메인 위젯 — 개선계획(improvement-plan).
 *
 * 서버 seed 조립이 `improvementPlan: {}`(빈 객체)를 주는 순수 사용자 입력 필드로,
 * 이 위젯은 **값 객체 편집만** 담당한다(표시 format은 템플릿 셀이
 * `{improvementPlan.department}({improvementPlan.person.name}) / {improvementPlan.schedule}`로 처리).
 *
 * 값 형태: { department?: string; person?: { name?: string }; schedule?: string }
 * - 3필드 입력(부서 / 담당자 / 일정 — 일정은 텍스트 입력, 날짜 포맷 강제 없음).
 * - 불변 갱신으로 onChange(중첩 person.name 부분 갱신 시 기존 값 보존).
 * - editable=false면 읽기 요약(`부서(담당) / 일정`, 빈 값 항목 생략).
 *
 * RegisteredRowWidget 계약(value/onChange/editable)만 구현 — 코어에 도메인 로직 없음.
 */
import type { RegisteredRowWidget } from '../RowsCollectionEditor';

interface ImprovementPlanValue {
  department?: string;
  person?: { name?: string };
  schedule?: string;
}

/** 불명확한 값을 개선계획 값 객체로 정규화(객체가 아니면 빈 객체). */
function asValue(value: unknown): ImprovementPlanValue {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as ImprovementPlanValue;
  }
  return {};
}

// RowsCollectionEditor의 셀 스타일과 톤을 맞춘 컴팩트 입력/읽기 표시 클래스.
const inputClass =
  'w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500';
const subLabelClass = 'block text-[11px] font-medium text-gray-500 mb-0.5';
// min-h-[2rem]는 admin CSS에 없던 유틸 — rc Tailwind 규약(임계 스타일 인라인)에 따라 style로 부여.
const readonlyValueClass =
  'w-full px-2 py-1.5 border border-gray-200 bg-gray-50 rounded text-sm text-gray-500 whitespace-pre-wrap break-words';
const readonlyValueStyle = { minHeight: '2rem' } as const;

/** 읽기 요약 텍스트: `부서(담당) / 일정` — 빈 값 항목은 생략. */
function summarize(v: ImprovementPlanValue): string {
  const dept = (v.department ?? '').trim();
  const person = (v.person?.name ?? '').trim();
  const schedule = (v.schedule ?? '').trim();
  const who = `${dept}${person ? `(${person})` : ''}`;
  const parts = [who, schedule].filter((s) => s !== '');
  return parts.length > 0 ? parts.join(' / ') : '—';
}

const ImprovementPlanWidget: RegisteredRowWidget = ({ value, onChange, editable }) => {
  const current = asValue(value);

  if (!editable) {
    return (
      <div className={readonlyValueClass} style={readonlyValueStyle}>
        {summarize(current)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <div>
        <label className={subLabelClass}>부서</label>
        <input
          type="text"
          className={inputClass}
          aria-label="부서"
          value={current.department ?? ''}
          onChange={(e) => onChange({ ...current, department: e.target.value })}
        />
      </div>
      <div>
        <label className={subLabelClass}>담당자</label>
        <input
          type="text"
          className={inputClass}
          aria-label="담당자"
          value={current.person?.name ?? ''}
          onChange={(e) =>
            onChange({ ...current, person: { ...(current.person ?? {}), name: e.target.value } })
          }
        />
      </div>
      <div>
        <label className={subLabelClass}>일정</label>
        <input
          type="text"
          className={inputClass}
          aria-label="일정"
          value={current.schedule ?? ''}
          onChange={(e) => onChange({ ...current, schedule: e.target.value })}
        />
      </div>
    </div>
  );
};

export default ImprovementPlanWidget;
